import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ControlBarButton } from '@m12s/component-library';
import { user } from 'lib/icons';
import { UtilityMenu } from 'components/UtilityMenu';
import useSelectOperator from 'lib/hooks/useSelectOperator';
import { getMachine } from 'lib/selectors/getMachine';
import { getHasOpenJob } from 'lib/selectors/getHasOpenJob';
import Routes from 'lib/utils/routes';
import {
  ControlBarResponsive,
  MobileIcon,
  ResponsiveText,
  SingleButtonScrollable,
} from '../styled';

/**
 * Control bar component for signed-out state in non-ERP mode.
 *
 * This component is rendered when:
 * - No operator is signed in
 * - System is NOT in ERP-driven mode
 * - No job is currently running
 *
 * In this state, the user must sign in before they can start working.
 * If there's no open job, signing in will redirect to job selection.
 *
 * @returns {JSX.Element} Control bar with sign-in button
 */
const SignedOut = () => {
  const { t } = useTranslation();
  const machine = useSelector(getMachine);
  const hasOpenJob = useSelector(getHasOpenJob);
  const { openSelectOperator } = useSelectOperator();

  return (
    <ControlBarResponsive id="job-control-bar">
      <UtilityMenu />
      <SingleButtonScrollable>
        <ControlBarButton
          id="job-control-bar-sign-in"
          onClick={() => {
            // If no job is open, redirect to job selection after sign-in
            if (!hasOpenJob) {
              return openSelectOperator({
                redirectPath: Routes.machineIdSelectJobPath(machine.id),
              });
            }

            // Otherwise, just open operator selection
            return openSelectOperator();
          }}
        >
          <MobileIcon icon={user} />
          <ResponsiveText
            full={t('signIn|full')}
            medium={t('signIn|medium')}
            short={t('signIn|short')}
          />
        </ControlBarButton>
      </SingleButtonScrollable>
    </ControlBarResponsive>
  );
};

export { SignedOut };
